<template>
  <div>
    <base-header class="pb-4">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">Partes</h6>
          <p class="text-white mb-0 mt-1 small opacity-8">
            Vista SAT: partes, material MISEPIS y fichajes FREDO
          </p>
        </div>
        <div class="col-lg-6 col-5 text-right">
          <base-button size="sm" type="neutral" @click="loadData(false)" :loading="loading">
            <i class="fas fa-sync-alt mr-2"></i>Actualizar
          </base-button>
          <base-button
            size="sm"
            type="primary"
            @click="loadData(true)"
            :loading="loading"
            class="ml-2"
          >
            <i class="fas fa-cloud-download-alt mr-2"></i>Refrescar SAT
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--4">
      <div class="row">
        <div class="col">
          <card class="no-border-card" body-classes="px-0 pb-1">
            <div class="p-3">
              <div class="row mb-3">
                <div class="col-md-2">
                  <label class="form-control-label">Mes</label>
                  <input
                    v-model="mes"
                    type="month"
                    class="form-control"
                    @change="onMesChange"
                  />
                </div>
                <div class="col-md-2">
                  <label class="form-control-label">Buscar</label>
                  <input
                    v-model="search"
                    type="text"
                    class="form-control"
                    placeholder="Nº parte, cliente..."
                    @keyup.enter="applyFilters"
                  />
                </div>
                <div class="col-md-2">
                  <label class="form-control-label">Estado SAT</label>
                  <select v-model="estadoSat" class="form-control" @change="applyFilters">
                    <option
                      v-for="opt in estadoSatOpciones"
                      :key="opt.value || 'todos'"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </div>
                <div class="col-md-2">
                  <label class="form-control-label">Estado albarán</label>
                  <select v-model="estado" class="form-control" @change="applyFilters">
                    <option value="">Todos</option>
                    <option value="pendiente_asignar">Pendiente asignar</option>
                    <option value="asignado">Asignado</option>
                    <option value="entregado">Entregado</option>
                    <option value="devuelto">Devuelto</option>
                    <option value="cerrado">Cerrado</option>
                  </select>
                </div>
                <div class="col-md-2">
                  <label class="form-control-label">Tarea facturación</label>
                  <select v-model="tareaFacturacion" class="form-control">
                    <option
                      v-for="opt in tareaFacturacionOpciones"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </div>
                <div class="col-md-2 d-flex align-items-end">
                  <base-button block type="primary" @click="applyFilters" :loading="loading">
                    Filtrar
                  </base-button>
                </div>
              </div>

              <div class="row mb-3 align-items-center">
                <div class="col-md-6">
                  <p class="text-muted small mb-0">
                    Solo se muestran albaranes con parte SAT vinculado.
                  </p>
                </div>
                <div class="col-md-6 text-md-right">
                  <div class="btn-group btn-group-sm" role="group">
                    <button
                      type="button"
                      class="btn"
                      :class="currentView === 'list' ? 'btn-primary' : 'btn-outline-primary'"
                      @click="currentView = 'list'"
                    >
                      <i class="fas fa-list mr-1"></i>Lista
                    </button>
                    <button
                      type="button"
                      class="btn"
                      :class="currentView === 'calendar' ? 'btn-primary' : 'btn-outline-primary'"
                      @click="currentView = 'calendar'"
                    >
                      <i class="fas fa-calendar-alt mr-1"></i>Calendario
                    </button>
                  </div>
                </div>
              </div>

              <div v-if="error" class="alert alert-danger">{{ error }}</div>

              <div v-if="meta" class="mb-3 text-muted small">
                {{ filteredItems.length }} partes visibles · {{ meta.total }} con parte SAT en
                {{ meta.mes }}
              </div>

              <div v-loading="loading">
                <div v-if="currentView === 'list'">
                  <div v-if="!filteredItems.length" class="text-center text-muted py-5">
                    No hay partes para los filtros seleccionados
                  </div>
                  <parte-list-card
                    v-for="item in filteredItems"
                    :key="item.albaran.id"
                    :item="item"
                    @click="openParte"
                  />
                </div>

                <partes-calendar
                  v-else
                  :items="filteredItems"
                  :mes="mes"
                  @select="openParte"
                  @month-change="onCalendarMonthChange"
                />
              </div>
            </div>
          </card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import ParteListCard from './components/ParteListCard.vue';
import PartesCalendar from './components/PartesCalendar.vue';
import {
  TAREA_FACTURACION_OPCIONES,
  ESTADO_SAT_OPCIONES,
  filterItems,
} from '@/util/parteSat';

export default {
  name: 'PartesPage',
  components: {
    ParteListCard,
    PartesCalendar,
  },
  data() {
    return {
      mes: moment().format('YYYY-MM'),
      search: '',
      estado: '',
      estadoSat: '',
      tareaFacturacion: '',
      currentView: 'list',
      tareaFacturacionOpciones: TAREA_FACTURACION_OPCIONES,
      estadoSatOpciones: ESTADO_SAT_OPCIONES,
    };
  },
  computed: {
    ...mapGetters('vistaOperativa', ['items', 'meta', 'loading', 'error']),
    filteredItems() {
      return filterItems(this.items, {
        search: this.search,
        estadoAlbaran: this.estado,
        estadoSat: this.estadoSat,
        tareaFacturacion: this.tareaFacturacion,
      });
    },
  },
  mounted() {
    this.loadData(false);
  },
  methods: {
    openParte(item) {
      if (!item || !item.albaran) return;
      this.$router.push(`/partes/${item.albaran.id}`);
    },
    onMesChange() {
      this.loadData(false);
    },
    onCalendarMonthChange(newMes) {
      if (newMes && newMes !== this.mes) {
        this.mes = newMes;
        this.loadData(false);
      }
    },
    applyFilters() {
      this.loadData(false);
    },
    async loadData(refreshSat = false) {
      try {
        await this.$store.dispatch('vistaOperativa/fetchList', {
          mes: this.mes,
          search: this.search,
          estado: this.estado,
          estadoSat: this.estadoSat,
          refresh: refreshSat,
        });
      } catch (e) {
        this.$notify({
          type: 'danger',
          message: this.error || 'Error al cargar partes',
        });
      }
    },
  },
};
</script>
